import { Icon, InputGroup, Spinner } from '@cad-arc';
import { DebouncedFunc } from 'lodash';
import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import styles from './narrative-input.module.css';
import {
    LexicalComposer,
    PlainTextPlugin,
    ContentEditable,
    OnChangePlugin,
    ClearEditorPlugin,
    LexicalErrorBoundary,
    HistoryPlugin,
    $generateHtmlFromNodes,
    CommandListenersPlugin,
    EditorControl,
    EditorControlPlugin,
    FlagNodes,
    FlagPlugin,
    MaxLengthPlugin,
} from '../../../common/components/lexical';
import { OnLexicalError } from '../../../common/components/lexical/types';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features';
import { htmlToText } from 'html-to-text';
import { useNarrativeOverwriteSensor } from '../../hooks/use-narrative-overwrite-sensor';

const DEFAULT_NARRATIVE_INPUT_FIELD_NAME = 'narrative-input';

export type NarrativeInputFieldValues = FieldValues;
interface NarrativeInputProps extends React.ComponentProps<'div'> {
    isNewEvent?: boolean;
    fieldName?: string;
    agencyEventId: number;
    control: Control<NarrativeInputFieldValues, unknown>;
    editorControlRef: React.MutableRefObject<EditorControl | undefined>;
    isHighPriority?: boolean;
    onLexicalError?: OnLexicalError;
    onReplaceVehicles?: (vehicles: string[]) => void | DebouncedFunc<(vehicles: string[]) => void>;
    onSubmit?: () => void;
    setIsHighPriority?: (value: boolean) => void;
}

/**
 * @deprecated Use NarrativeInputNext instead
 *
 * TODO: Remove this component
 */
export const NarrativeInput = ({
    isNewEvent,
    fieldName,
    agencyEventId,
    className,
    control,
    editorControlRef,
    isHighPriority,
    onLexicalError,
    onReplaceVehicles,
    onSubmit,
    setIsHighPriority,
    ...props
}: NarrativeInputProps): React.JSX.Element => {
    const inputGroupRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<string>('');
    const canSetHighPriority = useEnabledFeatures()?.['usersCanChangeUpdatePriority'] ?? true;
    const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
    const [isFocussed, setIsFocussed] = useState<boolean>(false);

    const { narrativeOverwriteSensor } = useNarrativeOverwriteSensor(agencyEventId);

    return (
        <InputGroup
            ref={inputGroupRef}
            className={classnames(
                styles['root'],
                {
                    [styles['high-priority']]: !!isHighPriority,
                    [styles['read-only']]: isReadOnly,
                    [styles['focussed']]: isFocussed,
                },
                className
            )}
            data-inputid={`${fieldName ?? DEFAULT_NARRATIVE_INPUT_FIELD_NAME}-${agencyEventId}`}
            {...props}
        >
            {!isNewEvent && canSetHighPriority && (
                <div
                    onClick={() => {
                        if (editorControlRef.current?.isEditable()) {
                            setIsHighPriority?.(!isHighPriority);
                            editorControlRef.current?.focus();
                        }
                    }}
                    data-testid="ADDITIONAL_NARRATIVE_INPUT_PRIORITY_INDICATOR"
                    data-is-high-priority={!!isHighPriority}
                    className={styles['priority-button']}
                >
                    <Icon
                        icon="ExclamationPoint"
                        data-testid="ADDITIONAL_NARRATIVE_INPUT_PRIORITY_INDICATOR_ENABLED"
                    />
                </div>
            )}
            <Controller
                control={control}
                name={`${fieldName ?? DEFAULT_NARRATIVE_INPUT_FIELD_NAME}`}
                defaultValue=""
                render={({ field: { onChange, onBlur } }) => (
                    <LexicalComposer
                        initialConfig={{
                            onError(error) {
                                onLexicalError?.({
                                    error,
                                    agencyEventId,
                                    message: 'Additional infos input, Lexical error',
                                });
                            },
                            nodes: [...FlagNodes],
                            namespace: 'additional-infos',
                        }}
                    >
                        <>
                            <PlainTextPlugin
                                contentEditable={
                                    <ContentEditable
                                        className={classnames(styles['input'], {
                                            [styles['new-event-narrative-input']]: isNewEvent,
                                        })}
                                        data-testid="ADDITIONAL_NARRATIVE_INPUT"
                                    />
                                }
                                placeholder={null}
                                ErrorBoundary={LexicalErrorBoundary}
                            />
                            <OnChangePlugin
                                onChange={(editorState, editor) => {
                                    // This hooks up the Lexical editor to the React Hook Form. When there's a change
                                    // to the Lexical editor, this callback is fired, the Lexical (node-based) data is
                                    // converted to plain text and sent to React Hook Form via its onChange callback.
                                    editorState.read(() => {
                                        const html = $generateHtmlFromNodes(editor, null);
                                        const text = htmlToText(html, {
                                            preserveNewlines: true,
                                            whitespaceCharacters: '',
                                        });
                                        narrativeOverwriteSensor({
                                            prevContent: contentRef.current,
                                            newContent: text,
                                        });
                                        contentRef.current = text;
                                        onChange(text);
                                    });
                                }}
                                ignoreSelectionChange
                            />
                            <CommandListenersPlugin
                                onBlur={() => {
                                    setIsFocussed(false);
                                    onBlur();
                                }}
                                onEditable={(isEditable) => {
                                    if (isEditable) {
                                        setIsReadOnly(false);
                                    } else {
                                        setIsReadOnly(true);
                                    }
                                }}
                                onEnterDown={() => {
                                    if (
                                        onSubmit &&
                                        editorControlRef.current?.isEditable() &&
                                        contentRef.current &&
                                        contentRef.current.trim() !== ''
                                    ) {
                                        onSubmit();
                                        editorControlRef.current?.setEditable(false);
                                    }
                                }}
                                onFocus={() => {
                                    setIsFocussed(true);
                                }}
                            />
                            <EditorControlPlugin ref={editorControlRef} />
                            {/* FlagPlugin renders the caution and plate flags in the editor */}
                            <FlagPlugin onReplaceVehicles={onReplaceVehicles} />
                            <MaxLengthPlugin maxLength={1024 * 64} />
                            {/* ClearEditorPlugin add support to clear the editor via dispatching CLEAR_EDITOR_COMMAND */}
                            <ClearEditorPlugin />
                            {/* HistoryPlugin allows undo/redo keypress in the editor */}
                            <HistoryPlugin />
                        </>
                    </LexicalComposer>
                )}
            />
            {!isNewEvent && <Spinner size="sm" className={styles['spinner']} />}
        </InputGroup>
    );
};
